<template>
  <v-dialog v-model="internalValue" max-width="620px" scrollable>
    <v-card class="ds-dialog">
      <v-card-title class="ds-dialog-header d-flex justify-space-between align-center">
        <div class="d-flex align-center">
          <v-icon color="white" class="mr-2">mdi-link-variant</v-icon>
          <span class="ds-dialog-title">Vincular</span>
        </div>
        <v-btn icon color="white" class="close-btn" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="ds-dialog-body">
        <!-- Vínculos atuais (de qualquer tipo) -->
        <section class="ds-form-section">
          <div class="ds-form-section__head">
            <div class="ds-form-section__title">
              <v-icon small color="primary" class="mr-2">mdi-link-variant</v-icon>
              <span>Vinculado a</span>
            </div>
          </div>
          <v-alert dense text type="info" class="mb-3 caption">
            Vinculando <strong>{{ sourceLabel }}</strong>
            <span class="grey--text">({{ typeShort(sourceType) }})</span>
          </v-alert>
          <div v-if="loading" class="py-2">
            <v-progress-circular indeterminate size="20" width="2" color="primary" />
          </div>
          <div v-else-if="links.length === 0" class="caption grey--text">
            Nenhum vínculo ainda.
          </div>
          <div v-else>
            <v-chip
              v-for="link in links"
              :key="link.correlation_id"
              class="ma-1"
              color="primary"
              text-color="white"
              small
              close
              @click:close="remove(link)"
            >
              <span class="chip-type mr-1">{{ typeShort(link.other.type) }}</span>
              <strong v-if="link.other.code" class="mr-1">{{ link.other.code }}</strong>
              <span class="ds-chip-label" :title="labelOf(link.other)">{{ labelOf(link.other) }}</span>
            </v-chip>
          </div>
        </section>

        <!-- Buscar + candidatos agrupados por tipo -->
        <section class="ds-form-section">
          <div class="ds-form-section__head">
            <div class="ds-form-section__title">
              <v-icon small color="primary" class="mr-2">mdi-magnify</v-icon>
              <span>Buscar</span>
            </div>
          </div>
          <v-text-field
            v-model="search"
            dense
            outlined
            hide-details
            clearable
            prepend-inner-icon="mdi-magnify"
            placeholder="Buscar em tudo que o paciente tem..."
            class="mb-3"
          />

          <div v-if="loadingPool" class="py-2 text-center">
            <v-progress-circular indeterminate size="22" width="2" color="primary" />
          </div>
          <div v-else-if="groupedCandidates.length === 0" class="caption grey--text">
            Nada disponível para vincular.
          </div>
          <div v-else>
            <div v-for="group in groupedCandidates" :key="group.type" class="mb-2">
              <p class="candidate-group-title">
                {{ typeLabel(group.type) }}
                <span class="grey--text">({{ group.items.length }})</span>
              </p>
              <v-chip
                v-for="item in group.items"
                :key="group.type + '-' + item.id"
                class="ma-1"
                outlined
                color="primary"
                small
                :disabled="saving"
                @click="add(item)"
              >
                <v-icon left x-small>mdi-plus</v-icon>
                <strong v-if="item.code" class="mr-1">{{ item.code }}</strong>
                {{ item.label }}
              </v-chip>
            </div>
          </div>
        </section>
      </v-card-text>

      <v-card-actions class="ds-dialog-actions">
        <v-btn outlined color="primary" @click="close">
          <v-icon left>mdi-close</v-icon>
          Fechar
        </v-btn>
        <v-spacer></v-spacer>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { useSnackbar } from "@/composables/useSnackbar";
import {
  linkCorrelation,
  unlinkCorrelation,
  listCorrelations,
  getLinkableEntities,
} from "@/services/correlationService";

// Rótulos por tipo (morph) para agrupar/exibir.
const TYPE_LABELS = {
  condition: "CIDs (crônicas / transtornos)",
  main_goal: "Objetivo principal",
  goal: "Metas e prazos",
  medication: "Medicamentos",
  supplement: "Suplementos",
  allergy: "Alergias",
  symptom: "Sintomas",
  restriction: "Restrições alimentares",
  complementary_treatment: "Tratamentos complementares",
  exam: "Exames",
  prescription_item: "Itens de prescrição",
};
const TYPE_SHORT = {
  condition: "CID",
  main_goal: "Objetivo",
  goal: "Meta",
  medication: "Medicamento",
  supplement: "Suplemento",
  allergy: "Alergia",
  symptom: "Sintoma",
  restriction: "Restrição",
  complementary_treatment: "Trat.",
  exam: "Exame",
  prescription_item: "Presc.",
};

export default {
  name: "CorrelationLinker",

  props: {
    value: { type: Boolean, default: false },
    sourceType: { type: String, required: true },
    sourceId: { type: [String, Number], default: null },
    sourceLabel: { type: String, default: "" },
    // paciente dono (para buscar tudo que ele tem)
    patientId: { type: [String, Number], default: null },
  },

  setup() {
    const { success, error, erroDeRequisicao } = useSnackbar();
    return { success, error, erroDeRequisicao };
  },

  data() {
    return {
      links: [],
      pool: [],
      search: "",
      loading: false,
      loadingPool: false,
      saving: false,
    };
  },

  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },

    // chaves "tipo:id" já vinculadas (para não repetir nos candidatos)
    linkedKeys() {
      return new Set(this.links.map((l) => `${l.other.type}:${l.other.id}`));
    },

    // candidatos = tudo do paciente, menos a própria origem e os já vinculados,
    // filtrado pela busca, agrupado por tipo.
    groupedCandidates() {
      const q = (this.search || "").toLowerCase().trim();
      const filtered = this.pool.filter((it) => {
        if (it.type === this.sourceType && String(it.id) === String(this.sourceId)) {
          return false; // não vincular consigo mesmo
        }
        if (this.linkedKeys.has(`${it.type}:${it.id}`)) {
          return false; // já vinculado
        }
        if (q) {
          const hay = `${it.label} ${it.code || ""}`.toLowerCase();
          if (!hay.includes(q)) return false;
        }
        return true;
      });

      const groups = {};
      for (const it of filtered) {
        (groups[it.type] = groups[it.type] || []).push(it);
      }
      // ordena os grupos pela ordem de TYPE_LABELS
      return Object.keys(TYPE_LABELS)
        .filter((t) => groups[t] && groups[t].length)
        .map((t) => ({ type: t, items: groups[t] }));
    },
  },

  watch: {
    value(open) {
      if (open && this.sourceId) {
        this.load();
        this.loadPool();
      }
    },
  },

  methods: {
    typeLabel(t) {
      return TYPE_LABELS[t] || t;
    },
    typeShort(t) {
      return TYPE_SHORT[t] || t;
    },
    labelOf(o) {
      return (
        o.label ||
        o.description ||
        o.name ||
        o.goal ||
        o.goal_name ||
        o.allergen ||
        o.symptom ||
        o.restriction ||
        o.treatment ||
        o.exam_name ||
        `#${o.id}`
      );
    },

    async load() {
      this.loading = true;
      try {
        this.links = await listCorrelations(this.sourceType, this.sourceId);
      } catch (err) {
        console.error("Erro ao carregar correlações:", err);
        this.links = [];
      } finally {
        this.loading = false;
      }
    },

    async loadPool() {
      if (!this.patientId) {
        this.pool = [];
        return;
      }
      this.loadingPool = true;
      try {
        this.pool = await getLinkableEntities(this.patientId);
      } catch (err) {
        console.error("Erro ao carregar itens vinculáveis:", err);
        this.pool = [];
      } finally {
        this.loadingPool = false;
      }
    },

    async add(item) {
      this.saving = true;
      try {
        await linkCorrelation({
          source_type: this.sourceType,
          source_id: this.sourceId,
          target_type: item.type,
          target_id: item.id,
          relation_type: "relacionado",
        });
        await this.load();
        this.$emit("changed");
      } catch (err) {
        console.error("Erro ao vincular:", err);
        this.erroDeRequisicao(err, err.response?.data?.message || "Erro ao vincular");
      } finally {
        this.saving = false;
      }
    },

    async remove(link) {
      // salvia-776: desvinculava SEM perguntar nada. O vinculo e informacao
      // clinica -- dizer que um sintoma vem de um medicamento, ou que uma
      // condicao trata outra, e uma afirmacao do profissional sobre o caso.
      const ok = await this.$confirm({
        type: "delete",
        title: "Remover vínculo",
        message: "Remover este vínculo entre os registros?",
        confirmText: "Remover",
        hint: "Os dois registros continuam existindo; só a ligação entre eles é desfeita.",
      });
      if (!ok) return;

      try {
        await unlinkCorrelation(link.correlation_id);
        await this.load();
        this.$emit("changed");
      } catch (err) {
        console.error("Erro ao remover vínculo:", err);
        this.erroDeRequisicao(err, err.response?.data?.message || "Erro ao remover");
      }
    },

    close() {
      this.internalValue = false;
    },
  },
};
</script>

<style scoped>
.ds-dialog {
  border-radius: 4px !important;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(31, 77, 63, 0.18) !important;
}
.ds-dialog-header {
  background: linear-gradient(135deg, var(--ds-primary) 0%, var(--ds-primary-deep) 100%);
  color: var(--ds-white);
}
.ds-dialog-title {
  font-size: 16px;
  font-weight: 700;
}
.close-btn {
  position: relative;
  z-index: 1;
}
.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.15) !important;
}
.candidate-group-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--ds-primary, var(--ds-primary));
  margin: 6px 0 2px 0;
}
.chip-type {
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  opacity: 0.8;
  border-right: 1px solid rgba(255, 255, 255, 0.4);
  padding-right: 5px;
}
</style>
